import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Loads data from the API. If the API cannot be reached and a fallback is given,
 * the fallback is shown and `usingFallback` is true so the page can say so.
 *
 * The data is loaded when the page opens and loaded again, quietly, whenever the browser
 * tab regains focus (so rows added in the database show up when you come back to the tab).
 * Pass `{ refreshMs }` to also poll on a timer, and `{ deps: [slug] }` to load again when a route value changes.
 * `reload()` forces a refresh.
 */
export function useApiData(fetcher, fallback = null, { refreshMs = 0, deps = [] } = {}) {
  const [state, setState] = useState({ data: null, loading: true, error: null, usingFallback: false });
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;
  const alive = useRef(true);

  const load = useCallback(() => {
    fetcherRef
      .current()
      .then((data) => alive.current && setState({ data, loading: false, error: null, usingFallback: false }))
      .catch((error) => {
        if (!alive.current) return;
        if (fallbackRef.current !== null) {
          // Keep showing real data if we already had it; only fall back on the first failure.
          setState((s) => (s.data && !s.usingFallback ? s : { data: fallbackRef.current, loading: false, error: null, usingFallback: true }));
        } else {
          setState((s) => (s.data ? s : { data: null, loading: false, error, usingFallback: false }));
        }
      });
  }, []);

  useEffect(() => {
    alive.current = true;
    setState({ data: null, loading: true, error: null, usingFallback: false });
    load();
    const onVisible = () => document.visibilityState === "visible" && load();
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", onVisible);
    const timer = refreshMs > 0 ? setInterval(load, refreshMs) : null;
    return () => {
      alive.current = false;
      window.removeEventListener("focus", load);
      document.removeEventListener("visibilitychange", onVisible);
      if (timer) clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, refreshMs, ...deps]);

  return { ...state, reload: load };
}
